package ui.consumerInformation

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.flow.StateFlow
import model.Card
import model.DataRepository
import model.DeleteCard
import ui.pullToRefresh.PullRefreshIndicator
import ui.pullToRefresh.pullRefresh
import ui.pullToRefresh.rememberPullRefreshState
import ui.scrollbar.LazyColumnScrollbar
import ui.scrollbar.LazyRowScrollbar

@Composable
fun ColumnScope.ConsumerCardDisplay(cards: List<Card>, shouldShowScrollbar: Boolean) {
    Text(
        "My Cards",
        fontWeight = FontWeight.ExtraBold,
        modifier = Modifier.align(Alignment.Start).padding(start = 16.dp, top = 16.dp),
        fontSize = 20.sp
    )
    val lazyLisState = rememberLazyListState()
    val cardViewHeight = 126.dp //Intrinsic Height can't be used with lazyRows at this time
    LazyRowScrollbar(
        listState = lazyLisState,
        alwaysShowScrollBar = shouldShowScrollbar,
        height = cardViewHeight,
    ) {
        LazyRow(
            state = lazyLisState,
        ) {
            item {
                Spacer(modifier = Modifier.width(16.dp))
            }
            items(cards) { item ->
                if (item.cardId != -1) {
                    CardDisplayView(item, cardViewHeight)
                    VerticalDivider(
                        color = Color.Transparent,
                        thickness = 8.dp,
                    )
                }
            }
            item {
                Spacer(modifier = Modifier.width(8.dp))
            }
        }
    }
}

@Composable
fun ColumnScope.ConsumerCardDisplayColumn(
    cards: List<DeleteCard>,
    shouldShowScrollbar: Boolean,
    cardStateFlow: StateFlow<DataRepository.CardState>,
    onPullToRefresh: () -> Unit,
    onCardClicked: (DeleteCard) -> Unit,
) {
    val cardState by cardStateFlow.collectAsState()
    val isRefreshing = cardState is DataRepository.CardState.Refreshing
    val isError = cardState is DataRepository.CardState.Error

    Row(
        modifier = Modifier.fillMaxWidth().padding(start = 16.dp, end = 4.dp, top = 16.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            "Cards",
            fontWeight = FontWeight.ExtraBold,
            fontSize = 20.sp
        )
        if (shouldShowScrollbar) {
            // Desktop browsers have no touch/drag gesture to trigger pull-to-refresh, so
            // surface an explicit refresh action there instead.
            Spacer(modifier = Modifier.weight(1f))
            IconButton(onClick = onPullToRefresh, enabled = !isRefreshing) {
                if (isRefreshing) {
                    CircularProgressIndicator(modifier = Modifier.size(20.dp))
                } else {
                    Icon(Icons.Filled.Refresh, contentDescription = "Refresh cards")
                }
            }
        }
    }

    if (isError && cards.isEmpty()) {
        Text(
            "Unable to load cards. Tap refresh to try again.",
            color = Color.Red,
            modifier = Modifier.align(Alignment.Start).padding(start = 16.dp, top = 4.dp),
        )
    }

    Box(modifier = Modifier.fillMaxWidth()) {
        val lazyLisState = rememberLazyListState()

        val pullRefreshState = rememberPullRefreshState(
            refreshing = cardState is DataRepository.CardState.Refreshing,
            onRefresh = onPullToRefresh
        )

        Column {
            LazyColumnScrollbar(
                listState = lazyLisState,
                alwaysShowScrollBar = shouldShowScrollbar,
            ) {
                LazyColumn(state = lazyLisState, modifier = Modifier.pullRefresh(pullRefreshState)) {
                    item {
                        Spacer(modifier = Modifier.width(16.dp))
                    }
                    items(cards.sortedByDescending { it.updateDateTime }) { item ->
                        if (item.card.cardId != -1) {
                            DeleteCardDisplayView(item) {
                                onCardClicked(item)
                            }
                            VerticalDivider(
                                color = Color.Transparent,
                                thickness = 8.dp,
                            )
                        }
                    }
                    item {
                        Spacer(modifier = Modifier.width(8.dp))
                    }
                }
            }
        }

        PullRefreshIndicator(
            refreshing = cardState is DataRepository.CardState.Refreshing,
            state = pullRefreshState,
            modifier = Modifier.align(Alignment.TopCenter),
        )

    }

}